import { useId } from 'react'
import type { ReactNode } from 'react'
import { allTimeZones } from '../lib/zonedTime'

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="stack" style={{ gap: 4 }}>
      <span className="label" style={{ margin: 0 }}>
        {label}
      </span>
      {children}
      {hint && <span className="t-caption faint">{hint}</span>}
    </label>
  )
}

export function Row({ children, columns = 2 }: { children: ReactNode; columns?: number }) {
  return (
    <div
      className="form-row"
      style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 10 }}
    >
      {children}
    </div>
  )
}

export function Text({
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  autoFocus,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  type?: string
  required?: boolean
  autoFocus?: boolean
}) {
  return (
    <input
      className="field"
      type={type}
      value={value}
      placeholder={placeholder}
      required={required}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

export function TextArea({
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  rows?: number
  placeholder?: string
}) {
  return (
    <textarea
      className="field"
      rows={rows}
      value={value}
      placeholder={placeholder}
      style={{ resize: 'vertical' }}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

export function Select<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (next: T) => void
  options: ReadonlyArray<{ value: T; label: string }>
}) {
  return (
    <select
      className="field"
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

/**
 * Venue timezone.
 *
 * Every time on a tour is stored as an instant plus the zone it was entered in,
 * so this is not cosmetic — get it wrong and load-in moves.
 */
export function TimeZoneSelect({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  const listId = useId()
  return (
    <>
      <input
        className="field"
        list={listId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
      />
      <datalist id={listId}>
        {allTimeZones().map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
    </>
  )
}

/** Comma- or Enter-separated tags, used for crew roles. */
export function Tags({
  value,
  onChange,
  hint = 'Type one and press Enter.',
}: {
  value: string[]
  onChange: (next: string[]) => void
  /** Shown BELOW the input, never inside it — instructions that sit in the
   *  field disappear the moment someone starts typing, which is exactly when
   *  they are needed. */
  hint?: string
}) {
  return (
    <div className="stack" style={{ gap: 7 }}>
      {value.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {value.map((tag) => (
            <span key={tag} className="chip">
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                style={{ color: 'inherit', opacity: 0.7 }}
                onClick={() => onChange(value.filter((entry) => entry !== tag))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className="field"
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ',') return
          event.preventDefault()
          const entry = event.currentTarget.value.trim()
          if (!entry || value.includes(entry)) return
          onChange([...value, entry])
          event.currentTarget.value = ''
        }}
      />
      {hint && <span className="t-spec-caption faint">{hint}</span>}
    </div>
  )
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p role="alert" className="t-caption" style={{ color: 'var(--destructive)' }}>
      {children}
    </p>
  )
}
